import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, CarTask } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export const CAR_TASKS_KEY = 'carTasks';

export function useCarTasks(carId?: string | null) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const tasksQuery = useQuery({
    queryKey: [CAR_TASKS_KEY, carId],
    queryFn: async () => {
      if (!carId) return [];

      const { data, error } = await supabase
        .from('car_tasks')
        .select('*')
        .eq('car_id', carId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as CarTask[];
    },
    enabled: !!carId && !!session,
  });

  const createTask = useMutation({
    mutationFn: async (newTask: Omit<CarTask, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('car_tasks')
        .insert([newTask])
        .select()
        .single();
      
      if (error) throw error;
      return data as CarTask;
    },
    onSuccess: () => {
      if (carId) {
        queryClient.invalidateQueries({ queryKey: [CAR_TASKS_KEY, carId] });
      }
    },
  });

  const updateTask = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CarTask> }) => {
      const { data, error } = await supabase
        .from('car_tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as CarTask;
    },
    onSuccess: () => {
      if (carId) {
        queryClient.invalidateQueries({ queryKey: [CAR_TASKS_KEY, carId] });
      }
    },
  });

  const deleteTask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('car_tasks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      if (carId) {
        queryClient.invalidateQueries({ queryKey: [CAR_TASKS_KEY, carId] });
      }
    },
  });

  return {
    tasks: tasksQuery.data || [],
    isLoading: tasksQuery.isLoading,
    isError: tasksQuery.isError,
    error: tasksQuery.error,
    createTask,
    updateTask,
    deleteTask,
  };
}
